import { decodeTime } from "ulid";
import { MessageFlags } from "../hydration/message.js";
import { File } from "./File.js";
/**
 * Message Class
 */
export class Message {
    #collection;
    id;
    /**
     * Construct Message
     * @param collection Collection
     * @param id Message Id
     */
    constructor(collection, id) {
        this.#collection = collection;
        this.id = id;
    }
    /**
     * Whether this object exists
     */
    get $exists() {
        return !!this.#collection.getUnderlyingObject(this.id).id;
    }
    /**
     * Time when this message was posted
     */
    get createdAt() {
        return new Date(decodeTime(this.id));
    }
    /**
     * Absolute pathname to this message in the client
     */
    get path() {
        return `${this.channel?.path}/${this.id}`;
    }
    /**
     * URL to this message
     */
    get url() {
        return this.#collection.client.configuration?.app + this.path;
    }
    /**
     * Nonce value
     */
    get nonce() {
        return this.#collection.getUnderlyingObject(this.id).nonce;
    }
    /**
     * Id of channel this message was sent in
     */
    get channelId() {
        return this.#collection.getUnderlyingObject(this.id).channelId;
    }
    /**
     * Channel this message was sent in
     */
    get channel() {
        return this.#collection.client.channels.get(this.#collection.getUnderlyingObject(this.id).channelId);
    }
    /**
     * Server this message was sent in
     */
    get server() {
        return this.channel?.server;
    }
    /**
     * Member this message was sent by
     */
    get member() {
        return this.#collection.client.serverMembers.getByKey({
            server: this.channel?.serverId,
            user: this.authorId,
        });
    }
    /**
     * Id of user or webhook this message was sent by
     */
    get authorId() {
        return this.#collection.getUnderlyingObject(this.id).authorId;
    }
    /**
     * User this message was sent by
     */
    get author() {
        return this.#collection.client.users.get(this.#collection.getUnderlyingObject(this.id).authorId);
    }
    /**
     * Webhook information for this message
     */
    get webhook() {
        return this.#collection.getUnderlyingObject(this.id).webhook;
    }
    /**
     * Content
     */
    get content() {
        return this.#collection.getUnderlyingObject(this.id).content ?? "";
    }
    /**
     * Content converted to plain text
     */
    get contentPlain() {
        return this.#collection.client.markdownToText(this.content);
    }
    /**
     * System message content
     */
    get systemMessage() {
        return this.#collection.getUnderlyingObject(this.id).systemMessage;
    }
    /**
     * Attachments
     */
    get attachments() {
        return this.#collection.getUnderlyingObject(this.id).attachments;
    }
    /**
     * Time at which this message was edited
     */
    get editedAt() {
        return this.#collection.getUnderlyingObject(this.id).editedAt;
    }
    /**
     * Embeds
     */
    get embeds() {
        return this.#collection.getUnderlyingObject(this.id).embeds;
    }
    /**
     * IDs of users this message mentions
     */
    get mentionIds() {
        return this.#collection.getUnderlyingObject(this.id).mentionIds;
    }
    /**
     * IDs of roles this message mentions
     */
    get roleMentionIds() {
        return this.#collection.getUnderlyingObject(this.id).roleMentionIds;
    }
    /**
     * Roles this message mentions
     */
    get roleMentions() {
        return this.roleMentionIds
            ?.map((roleId) => this.server?.roles.get(roleId))
            .filter((role) => role);
    }
    /**
     * Whether this message mentions us
     */
    get mentioned() {
        return (!!(this.flags & MessageFlags.MentionsEveryone) ||
            !!(this.flags & MessageFlags.MentionsOnline) ||
            this.mentionIds?.includes(this.#collection.client.user.id) ||
            this.roleMentions?.some((role) => role.assigned) ||
            false);
    }
    /**
     * IDs of messages this message replies to
     */
    get replyIds() {
        return this.#collection.getUnderlyingObject(this.id).replyIds;
    }
    /**
     * Reactions
     */
    get reactions() {
        return this.#collection.getUnderlyingObject(this.id).reactions;
    }
    /**
     * Interactions
     */
    get interactions() {
        return this.#collection.getUnderlyingObject(this.id).interactions;
    }
    /**
     * Masquerade
     */
    get masquerade() {
        return this.#collection.getUnderlyingObject(this.id).masquerade;
    }
    /**
     * Whether this message is pinned
     */
    get pinned() {
        return this.#collection.getUnderlyingObject(this.id).pinned || false;
    }
    /**
     * Flags
     */
    get flags() {
        return this.#collection.getUnderlyingObject(this.id).flags || 0;
    }
    /**
     * Get the username for this message
     */
    get username() {
        const webhook = this.webhook;
        return (this.masquerade?.name ??
            (webhook
                ? webhook.name
                : (this.member?.nickname ?? this.author?.username)));
    }
    /**
     * Get the role colour for this message
     */
    get roleColour() {
        return this.masquerade?.colour ?? this.member?.roleColour;
    }
    /**
     * Get the role colour for this message
     */
    get iconRole() {
        return this.member?.iconRole;
    }
    /**
     * Get the avatar URL for this message
     */
    get avatarURL() {
        const webhook = this.webhook;
        return (this.masqueradeAvatarURL ??
            (webhook
                ? webhook.avatarURL
                : (this.member?.avatarURL ?? this.author?.avatarURL)));
    }
    /**
     * Get the animated avatar URL for this message
     */
    get animatedAvatarURL() {
        const webhook = this.webhook;
        return (this.masqueradeAvatarURL ??
            (webhook
                ? webhook.avatarURL
                : this.member
                    ? this.member?.animatedAvatarURL
                    : this.author?.animatedAvatarURL));
    }
    /**
     * Avatar URL from the masquerade
     */
    get masqueradeAvatarURL() {
        const avatar = this.masquerade?.avatar;
        return avatar ? this.#collection.client.proxyFile(avatar) : undefined;
    }
    /**
     * Whether this message has suppressed desktop/push notifications
     */
    get isSuppressed() {
        return (this.flags & 1) === 1;
    }
    /**
     * Edit a message
     * @param data Message edit route data
     */
    async edit(data) {
        return await this.#collection.client.api.patch(`/channels/${this.channelId}/messages/${this.id}`, data);
    }
    /**
     * Remove the automatically generated link previews from this message
     */
    async suppressEmbeds() {
        const request = this.#collection.client.api.delete;
        await request(`/channels/${this.channelId}/messages/${this.id}/embeds`);
    }
    /**
     * Delete a message
     */
    async delete() {
        return await this.#collection.client.api.delete(`/channels/${this.channelId}/messages/${this.id}`);
    }
    /**
     * Acknowledge this message as read
     * @param skipRateLimiter Whether to skip the internal rate limiter
     * @param skipRequest For internal updates only
     * @param skipNextMarking For internal usage only
     * @requires `SavedMessages`, `DirectMessage`, `Group`, `TextChannel`
     */
    ack(skipRateLimiter, skipRequest, skipNextMarking) {
        this.channel?.ack(this, skipRateLimiter, skipRequest, skipNextMarking);
    }
    /**
     * Reply to Message
     */
    reply(data, mention = true) {
        const obj = typeof data === "string" ? { content: data } : data;
        return this.channel?.sendMessage({
            ...obj,
            replies: [{ id: this.id, mention }],
        });
    }
    /**
     * Clear all reactions from this message
     */
    async clearReactions() {
        return await this.#collection.client.api.delete(`/channels/${this.channelId}/messages/${this.id}/reactions`);
    }
    /**
     * React to a message
     * @param emoji Unicode or emoji ID
     */
    async react(emoji) {
        return await this.#collection.client.api.put(`/channels/${this.channelId}/messages/${this.id}/reactions/${emoji}`);
    }
    /**
     * Un-react from a message
     * @param emoji Unicode or emoji ID
     * @param deleteAll Remove all reactions
     */
    async unreact(emoji, deleteAll = false) {
        return await this.#collection.client.api.delete(`/channels/${this.channelId}/messages/${this.id}/reactions/${emoji}`, { remove_all: deleteAll });
    }
    /**
     * Pin the message
     */
    pin() {
        return this.#collection.client.api.post(`/channels/${this.channelId}/messages/${this.id}/pin`);
    }
    /**
     * Unpin the message
     */
    unpin() {
        return this.#collection.client.api.delete(`/channels/${this.channelId}/messages/${this.id}/pin`);
    }
}
/**
 * Message Webhook Class
 */
export class MessageWebhook {
    #client;
    id;
    name;
    avatar;
    /**
     * Construct Message Webhook
     * @param client Client
     * @param webhook Webhook data
     */
    constructor(client, webhook, id) {
        this.#client = client;
        this.id = id;
        this.name = webhook.name;
        this.avatar = webhook.avatar
            ? new File(client, {
                _id: webhook.avatar,
                tag: "avatars",
                metadata: {
                    type: "Image",
                    width: 256,
                    height: 256,
                },
            })
            : undefined;
    }
    /**
     * Get the avatar URL for this message webhook
     */
    get avatarURL() {
        return (this.avatar?.createFileURL() ??
            `${this.#client.options.baseURL}/users/${this.id}/default_avatar`);
    }
}
